import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'localization';
import './PublisherFeaturedSpotlightOrganism.scss';
import { usePublisherDetail } from '../../../hooks/usePublisherDetail';
import ScopeCoinAtom from '../../atoms/marketplace/ScopeCoinAtom';
import StatusBadgeAtom from '../../atoms/StatusBadge/StatusBadgeAtom';
import type { PublisherDetail } from 'types/publisher';

export interface PublisherFeaturedSpotlightOrganismProps {
  publisher: PublisherDetail;
  className?: string;
}

export const PublisherFeaturedSpotlightOrganism: React.FC<
  PublisherFeaturedSpotlightOrganismProps
> = ({ publisher, className = '' }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const { channels } = usePublisherDetail(publisher.slug);
  const previewChannels = channels.slice(0, 3);

  const sectionStyle = {
    '--publisher-hue': `${publisher.hue}`,
  } as React.CSSProperties;

  const classes = ['marketplace-pub-spotlight', className].filter(Boolean).join(' ');

  const handleSectionClick = () => {
    navigate(`/marketplace/publishers/${encodeURIComponent(publisher.slug)}`);
  };

  const handleChannelClick = (channelId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    navigate(`/marketplace/channels/${channelId}`);
  };

  return (
    <section
      className={classes}
      style={sectionStyle}
      onClick={handleSectionClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleSectionClick();
        }
      }}
      aria-label={t('marketplace.publishers.spotlight.ariaLabel')}
    >
      {/* Left: publisher info */}
      <div className="marketplace-pub-spotlight__left">
        {/* "Featured publisher" pill */}
        <span className="marketplace-pub-spotlight__pill">
          <i className="pi pi-sparkles" aria-hidden="true" />
          {t('marketplace.publishers.spotlight.featuredPill')}
        </span>

        {/* Glyph + name row */}
        <div className="marketplace-pub-spotlight__title-row">
          <ScopeCoinAtom
            scope="center"
            glyph={publisher.glyph}
            channelName={publisher.display_name}
            glyphKind={publisher.glyph_kind}
            hue={publisher.hue}
            size="lg"
          />
          <div className="marketplace-pub-spotlight__title-stack">
            <h2 className="marketplace-pub-spotlight__name">
              {publisher.display_name}
              {publisher.is_official && (
                <StatusBadgeAtom
                  tone="success"
                  icon="pi pi-verified"
                  label={t('marketplace.publishers.officialBadge')}
                  size="medium"
                  className="marketplace-pub-spotlight__official"
                />
              )}
            </h2>
            <span className="marketplace-pub-spotlight__slug">/{publisher.slug}</span>
          </div>
        </div>

        {/* Bio */}
        {publisher.bio && (
          <p className="marketplace-pub-spotlight__bio">{publisher.bio}</p>
        )}

        {/* Stats row */}
        <div className="marketplace-pub-spotlight__stats">
          <span className="marketplace-pub-spotlight__stat">
            <i className="pi pi-package" aria-hidden="true" />
            {t('marketplace.publishers.card.channelCount', { count: publisher.channelCount })}
          </span>
          <span className="marketplace-pub-spotlight__stat">
            <i className="pi pi-users" aria-hidden="true" />
            {(publisher.subscriberCount ?? 0).toLocaleString()}{' '}
            {t('marketplace.publishers.spotlight.subscribers')}
          </span>
        </div>
      </div>

      {/* Right: up to 3 channel preview tiles */}
      {previewChannels.length > 0 && (
        <div className="marketplace-pub-spotlight__channels">
          {previewChannels.map((ch) => (
            <div
              key={ch.id}
              className="marketplace-pub-spotlight__channel-tile"
              onClick={(e) => handleChannelClick(ch.id, e)}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  e.stopPropagation();
                  navigate(`/marketplace/channels/${ch.id}`);
                }
              }}
              aria-label={t('marketplace.publishers.spotlight.channelTileAriaLabel', {
                name: ch.name.default,
              })}
            >
              <ScopeCoinAtom
                scope={ch.scope}
                glyph={ch.glyph ?? undefined}
                channelName={ch.name.default}
                glyphKind="icon"
                size="sm"
              />
              <div className="marketplace-pub-spotlight__channel-info">
                <span className="marketplace-pub-spotlight__channel-name">{ch.name.default}</span>
                <span className="marketplace-pub-spotlight__channel-meta">
                  {(ch.subscriber_count ?? 0).toLocaleString()}{' '}
                  {t('marketplace.publishers.spotlight.subs')} ·{' '}
                  {ch.item_count ?? 0}{' '}
                  {t('marketplace.publishers.spotlight.items')}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}
    </section>
  );
};

export default PublisherFeaturedSpotlightOrganism;
